import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, KeyRound, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center p-4">
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <img src="/assets/logo-goodtime.png" alt="Goodtime" className="h-16 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Review Copilot</h1>
        <p className="text-slate-500 mt-1">{subtitle}</p>
      </div>
      <Card className="border-slate-100 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  </div>
);

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      // Backend always returns 200 — only network/server errors land here.
      toast.error('Une erreur est survenue, réessayez dans un instant.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell title="Email envoyé" subtitle="Vérifiez votre boîte de réception">
        <div className="text-center space-y-4 py-2">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Si un compte existe avec <strong>{email}</strong>, un lien de réinitialisation
            vient d'être envoyé. Le lien est valable <strong>1 heure</strong>.
          </p>
          <p className="text-xs text-slate-400">Pensez à vérifier vos spams.</p>
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#FF5A6E] hover:underline font-medium">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Mot de passe oublié" subtitle="Entrez votre email pour recevoir un lien">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="votre@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="forgot-email-input"
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
          disabled={loading || !email}
          data-testid="forgot-submit-btn"
        >
          <Mail className="mr-2 h-4 w-4" />
          {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
        </Button>
      </form>
      <div className="mt-4 text-center text-sm text-slate-500">
        <Link to="/login" className="inline-flex items-center gap-1 text-[#FF5A6E] hover:underline font-medium">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
        </Link>
      </div>
    </AuthShell>
  );
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Lien invalide — token manquant');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirm) {
      toast.error('Les deux mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success('Mot de passe mis à jour. Connectez-vous.');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Lien invalide" subtitle="">
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Ce lien de réinitialisation est invalide ou incomplet. Demandez-en un nouveau ci-dessous.
        </p>
        <Link to="/forgot-password">
          <Button className="w-full bg-[#FF5A6E] hover:bg-[#FF3B55] text-white">
            Demander un nouveau lien
          </Button>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Nouveau mot de passe" subtitle="Choisissez un mot de passe">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">Nouveau mot de passe</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              data-testid="reset-password-input"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-slate-400">8 caractères minimum.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirmation</Label>
          <Input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            data-testid="reset-confirm-input"
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
          disabled={loading || !password || !confirm}
          data-testid="reset-submit-btn"
        >
          <KeyRound className="mr-2 h-4 w-4" />
          {loading ? 'Mise à jour...' : 'Réinitialiser le mot de passe'}
        </Button>
      </form>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
